import { FormikValues } from 'formik'

import { emptyCharacter } from '../../components/EmptyChar'
import { momentDateHandler } from '../../date/moment-date-handler'
import { formatCurrency, toFixed } from '../../formatters'
import { InstrumentCode } from '../../models'

const { newDate, formatDate, formatTime, sortByDate } = momentDateHandler

export type IssuerCode = 'CBA' | 'CNA' | 'WBC'

export type QuoteStatus = 'accepted' | 'confirmed' | 'pending' | 'rejected'

export interface RfqQuote {
  issuerCode: IssuerCode
  isWinningTenor: boolean
  status: QuoteStatus
  coupon: number
  bankCoupon: number
  tenor: string
  maturityDate: Date
  receivedAt: Date
  respondedBy?: string
}

export interface Issuer {
  id: number
  issuerCode: IssuerCode
  issuerName: string
  shortName: string
  quotes: RfqQuote[]
}

export type RfqStatuses = 'open' | 'closed' | 'cancelled'

export interface Rfq {
  basisPointFee: number
  uuid: string
  domain: string
  segmentCode: string
  instrumentCode: InstrumentCode
  principal: number
  coupon: number
  tenantName: string
  isTrial: boolean
  status: RfqStatuses
  rfqConfirmed: boolean
  requestedAt: Date
  requestedBy: string
  acceptedAt: Date
  settlementDate: Date
  maturityDate: Date
  quotes: RfqQuote[]
  winningIssuer: Issuer
  winningIssuerCode: IssuerCode
  winningQuote: RfqQuote
  issuerCodes: IssuerCode[]
  issuers: Issuer[]
  adiFee: number
  bestExecution: boolean
  operation: string
  cancelledBy: string | null
  confirmedBy: string | null
  nominatedBy: string | null
  acceptedBy: string | null
  signoffCount: number
}

export interface RollbackRfqFormValues extends FormikValues {
  reason: string
}

const isWinningQuote = (quote: RfqQuote) => quote && ['accepted', 'confirmed'].includes(quote.status)

export const convertRfqsToTableItems = (rfqs?: Rfq[]) =>
  rfqs?.length
    ? rfqs
        .sort((rfq1: Rfq, rfq2: Rfq) =>
          sortByDate({ firstDate: newDate(rfq2.requestedAt), secondDate: newDate(rfq1.requestedAt) }),
        )
        .map((rfq: Rfq) => {
          const issuerCodes = rfq.issuers.map((issuer) => issuer.issuerCode)

          const winningIssuer =
            rfq.status === 'closed' && rfq.issuers.find((issuer) => issuer.quotes.some(isWinningQuote))

          const winningQuote = winningIssuer && winningIssuer.quotes.find(isWinningQuote)

          const quotes = getQuotes(rfq.issuers, winningQuote)

          return {
            ...rfq,
            quotes,
            winningIssuer,
            winningIssuerCode: winningIssuer ? winningIssuer.issuerCode : null,
            issuerCodes,
            winningQuote,
            coupon: winningQuote ? winningQuote.coupon : null,
            bankCoupon: winningQuote ? winningQuote.bankCoupon : null,
            tenor: getTenor(winningQuote),
          }
        })
    : []

const getQuotes = (issuers, winningQuote) =>
  issuers.reduce(
    (issuerQuotes, issuer) =>
      issuerQuotes.concat(
        issuer.quotes.map((quote: RfqQuote, index: number) => ({
          issuerCode: issuer.issuerCode,
          isWinningTenor: quote.tenor === winningQuote.tenor,
          id: index,
          ...quote,
        })),
      ),
    [],
  )

const getTenor = (winningQuote) => {
  if (winningQuote?.tenor === 'CUSTOM') {
    return formatDate({ date: newDate(winningQuote.maturityDate) })
  }

  return winningQuote?.tenor || emptyCharacter
}

export const convertRfqsToExcel = (rfqs: Rfq[]) => {
  const data = rfqs.map((rfq) => {
    const isClosed = rfq.status === 'closed'

    return {
      tenant: rfq.domain,
      instrument: rfq.instrumentCode,
      requestedDate: formatDate({ date: newDate(rfq.requestedAt) }),
      requestedTime: formatTime({ date: newDate(rfq.requestedAt) }),
      principal: formatCurrency({ value: rfq.principal }),
      maturityDate: formatDate({ date: newDate(rfq.maturityDate) }),
      status: rfq.status,
      requested: rfq.issuerCodes.length,
      responded: getTotalResponded(rfq.issuerCodes, rfq),
      settlementDate: isClosed ? formatDate({ date: newDate(rfq.settlementDate) }) : null,
      acceptedAt: isClosed ? formatDate({ date: newDate(rfq.acceptedAt) }) : null,
      coupon: isClosed ? toFixed({ value: rfq.winningQuote.coupon }) : null,
      bestExecution: isClosed ? (rfq.bestExecution ? 'Yes' : 'Not best execution') : null,
      adiFee: isClosed ? formatCurrency({ value: rfq.adiFee }) : null,
      adi: rfq.winningIssuer.issuerCode,
      requestedBy: rfq.requestedBy,
      acceptedBy: isClosed ? rfq.acceptedBy : null,
    }
  })

  const headers = [
    'Tenant',
    'Asset Class',
    'Requested Date',
    'Requested Time',
    'Principal ($)',
    'Maturity Date',
    'Status',
    'Requested',
    'Responded',
    'Settlement Date',
    'Accepted at',
    'Winning Rate',
    'Best Execution',
    'ADI Fee',
    'Winning ADI',
    'Requested by',
    'Accepted by',
  ]

  return { data, headers }
}

const getTotalResponded = (issuerCodes, rfq: Rfq) =>
  issuerCodes.reduce((total, issuerCode) => {
    const quoted = rfq.quotes.filter((quote) => quote.issuerCode === issuerCode).some((quote) => !!quote.respondedBy)

    if (quoted) {
      total += 1
    }

    return total
  }, 0)
